/**
 * Centralized environment configuration
 *
 * This module provides a single source of truth for environment detection.
 *
 * Configuration priority:
 * 1. VITE_DEV_MODE environment variable (if explicitly set)
 * 2. Vite's built-in import.meta.env.DEV (build-time detection)
 * 3. Runtime window.openai check (fallback)
 */

/**
 * Determines if the application is running in development mode
 *
 * @returns true if in development mode, false otherwise
 */
export function isDevelopment(): boolean {
  // Priority 1: Check for explicit environment variable override
  if (import.meta.env.VITE_DEV_MODE !== undefined) {
    return import.meta.env.VITE_DEV_MODE === 'true';
  }

  // Priority 2: Use Vite's built-in dev mode detection
  if (import.meta.env.DEV !== undefined) {
    return import.meta.env.DEV;
  }

  // Priority 3: Fallback to runtime window.openai check
  if (typeof window !== 'undefined') {
    return !window.openai?.toolOutput;
  }

  // Default to production if we can't determine
  return false;
}

/**
 * Gets the current environment mode
 */
export function getEnvironment(): 'development' | 'production' {
  return isDevelopment() ? 'development' : 'production';
}

/**
 * Check if running in production mode
 */
export function isProduction(): boolean {
  return !isDevelopment();
}
